import FleetTitle from '@/libs/antd/components/Title';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Row,
	Select,
	Table,
	Typography,
} from 'antd';
import {
	FileSearchOutlined,
	EditOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import { FC, Fragment, useEffect } from 'react';
import { useApp } from '@/App';
import { useMutation } from '@tanstack/react-query';
import {
	searchGolive,
	SearchGoLiveParams,
	SearchGoLiveResponse,
	getGoliveBrand,
} from '@/libs/fleet-api/services/pipeline-golive';
import { useForm } from '@/libs/antd/form';
import rule from '@/libs/antd/rules';
import dayjs from 'dayjs';
import {
	useSelectDivision,
	useSelectPlafonType,
	useSelectSourceOrder,
} from '@/libs/fleet-api/hooks/master-data';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { formatCurrency } from '@/libs/antd/formater';
import BrandDetailModal, {
	useBrandDetailModal,
} from './modals/BrandDetailModal';
import {
	GetUserListPayload,
	v2GetUserLists,
	UserGroupName,
} from '@/libs/fleet-api/services/users';
import useQuerySelect from '@/hooks/useQuerySelect';
import { useUserData } from '@/libs/fleet-api/hooks';
import { useModals } from '@/components/modals/ModalProvider';
import { exportToExcel } from 'react-json-to-excel';

export const useSelectUserList = (
	params: GetUserListPayload,
	groupName?: UserGroupName,
) => {
	return useQuerySelect({
		queryKey: ['v2GetUserLists', params, groupName] as const,
		queryFn: async (_, keys) => {
			const [, params] = keys;
			const res = await v2GetUserLists(params);
			return res.data.map((data) => {
				return {
					label: data.fullName,
					value: data.userId,
					data,
				};
			});
		},
		infinite: false,
	});
};

const GoliveListPage: FC<{ defaultParams: SearchGoLiveParams }> = ({
	defaultParams,
}) => {
	const { groupName } = useUserData() ?? {};
	const { navigate, notification } = useApp();
	const { openCustomerDetail } = useModals();

	const { form, value, fieldName } = useForm<SearchGoLiveParams>();
	const { data, isPending, mutateAsync } = useMutation({
		mutationFn: searchGolive,
	});

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => form.submit(), []);

	const selectPlafond = useSelectPlafonType();
	const selectSourceOrder = useSelectSourceOrder(null);

	const selectFBDHUsers = useSelectUserList(
		{
			role: 'FBDH',
			divisionId: value?.divisionId,
		},
		groupName,
	);

	const selectFRMUsers = useSelectUserList(
		{
			role: 'FRM',
			reportToId: value?.fbdhId,
		},
		groupName,
	);

	const selectCCDHUsers = useSelectUserList(
		{
			role: 'CCDH',
		},
		groupName,
	);

	const selectCCAUsers = useSelectUserList(
		{
			role: 'CCA',
			reportToId: value?.ccdhId,
		},
		groupName,
	);

	const selectDivision = useSelectDivision();

	const getGoliveBrandMutation = useMutation({
		mutationFn: getGoliveBrand,
	});

	const brandDetailModal = useBrandDetailModal();
	const isAllowEdit = groupName === 'FRM' || groupName === 'FBDH';

	const isMasked = (status: string) => {
		return status === 'DRAFTED' && groupName !== 'FRM';
	};

	return (
		<Fragment>
			<FleetTitle>Pipeline Golive</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				colon={false}
				labelAlign="left"
				labelCol={{
					style: { width: 160 },
				}}
				style={{ marginBlock: 20 }}
				onFinish={(values) => mutateAsync(values)}
			>
				<Row
					gutter={60}
					align="bottom"
				>
					<Col
						flex="none"
						style={{
							width: 500,
							display: 'flex',
							flexDirection: 'column',
							gap: 20,
						}}
					>
						<Form.Item
							label="Tanggal"
							required
						>
							<Row
								wrap={false}
								align="middle"
								gutter={12}
							>
								<Col flex="auto">
									<Form.Item
										noStyle
										name={fieldName('startDate')}
										getValueProps={(value) => ({
											value: value && dayjs(value),
										})}
										normalize={(value) =>
											value && dayjs(value).utc(true).toISOString()
										}
										rules={[rule.required()]}
										initialValue={
											defaultParams?.startDate ??
											dayjs().utc(true).subtract(1, 'year').toISOString()
										}
									>
										<DatePicker />
									</Form.Item>
								</Col>
								<Col flex="none">-</Col>
								<Col flex="auto">
									<Form.Item
										noStyle
										name={fieldName('endDate')}
										getValueProps={(value) => ({
											value: value && dayjs(value),
										})}
										normalize={(value) =>
											value && dayjs(value).utc(true).toISOString()
										}
										rules={[rule.required()]}
										initialValue={
											defaultParams?.endDate ?? dayjs().utc(true).toISOString()
										}
									>
										<DatePicker />
									</Form.Item>
								</Col>
							</Row>
						</Form.Item>

						<Form.Item
							label="Jenis Plafon"
							name={fieldName('plafonTypeId')}
							initialValue={defaultParams?.plafonTypeId}
						>
							<Select {...selectPlafond} />
						</Form.Item>

						<Form.Item
							label="Sumber Order"
							name={fieldName('orderSourceId')}
							initialValue={defaultParams?.orderSourceId}
						>
							<Select {...selectSourceOrder} />
						</Form.Item>

						{groupName === 'FS' && (
							<Form.Item
								label="Divisi"
								name={fieldName('divisionId')}
								initialValue={defaultParams?.divisionId}
							>
								<Select {...selectDivision} />
							</Form.Item>
						)}

						{(groupName === 'FH' ||
							groupName === 'FS' ||
							groupName === 'CR') && (
							<Form.Item
								label="FBDH"
								name={fieldName('fbdhId')}
								initialValue={defaultParams?.fbdhId}
							>
								<Select
									{...selectFBDHUsers}
									onSelect={(value, option) => {
										selectFBDHUsers.onSelect!(value, option);
										form.setFieldValue(fieldName('frmId'), null);
									}}
									onClear={() => {
										form.setFieldValue(fieldName('fbdhId'), null);
										form.setFieldValue(fieldName('frmId'), null);
									}}
								/>
							</Form.Item>
						)}
						{(groupName === 'FBDH' ||
							groupName === 'FH' ||
							groupName === 'FS' ||
							groupName === 'CR') && (
							<Form.Item
								label="FRM"
								name={fieldName('frmId')}
								initialValue={defaultParams?.frmId}
							>
								<Select
									{...selectFRMUsers}
									onClear={() => {
										form.setFieldValue(fieldName('frmId'), null);
									}}
								/>
							</Form.Item>
						)}
						{(groupName === 'CH' || groupName === 'CR') && (
							<Form.Item
								label="CCDH"
								name={fieldName('ccdhId')}
								initialValue={defaultParams?.ccdhId}
							>
								<Select
									{...selectCCDHUsers}
									onSelect={(value, option) => {
										selectCCDHUsers.onSelect!(value, option);
										form.setFieldValue(fieldName('ccaId'), null);
									}}
									onClear={() => {
										form.setFieldValue(fieldName('ccdhId'), null);
										form.setFieldValue(fieldName('ccaId'), null);
									}}
								/>
							</Form.Item>
						)}
						{(groupName === 'CCDH' ||
							groupName === 'CH' ||
							groupName === 'CR') && (
							<Form.Item
								label="CCA"
								name={fieldName('ccaId')}
								initialValue={defaultParams?.ccaId}
							>
								<Select
									{...selectCCAUsers}
									onClear={() => {
										form.setFieldValue(fieldName('ccaId'), null);
									}}
								/>
							</Form.Item>
						)}
					</Col>
					<Col flex="none">
						<Button
							type="primary"
							htmlType="submit"
							style={{ width: 250 }}
							disabled={isPending}
						>
							SEARCH
						</Button>
					</Col>
				</Row>
			</Form>
			<Divider />
			<Typography.Title level={3}>
				{groupName === 'CCA' ||
				groupName === 'CCDH' ||
				groupName === 'FS' ||
				groupName === 'FH' ||
				groupName === 'CH'
					? 'Monitoring Pipeline Golive Debitur'
					: 'List Pipeline Golive Debitur'}
			</Typography.Title>
			<Table
				bordered
				loading={isPending}
				scroll={{ x: 'max-content' }}
				dataSource={
					data?.data
						? [
								...(data?.data.items.map((data, index) => {
									return {
										...data,
										key: index + 1,
									};
								}) ?? []),
								{
									key: 0,
									estimatedNtf: data?.data.totalEstimatedNtf ?? 0,
									units: data?.data.totalUnits ?? 0,
								} as unknown as SearchGoLiveResponse['items'][number] & {
									key: number;
								},
							]
						: []
				}
				rowHoverable={false}
				onRow={(record) => {
					if (record.key) return {};
					return {
						style: {
							background: '#CFE2FF',
						},
					};
				}}
				columns={[
					{
						title: isAllowEdit ? 'Edit' : '',
						align: 'center',
						width: 50,
						onCell: (record) => {
							if (record.key) return {};

							return {
								colSpan: 17,
							};
						},
						render: (_, { key, prospectId, prospectPlafonId }) => {
							if (!key) return <b>Total</b>;
							if (!isAllowEdit) return null;
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('GolivePage', {
											prospectId,
											prospectPlafonId,
											backParams: form.getFieldsValue(),
										});
									}}
								>
									<EditOutlined />
								</a>
							);
						},
					},
					{
						title: 'Pipeline Golive No',
						render: (_, record) => record.pipelineNo ?? '-',
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Prospect ID',
						render: (_, record) => record.prospectId,
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'NAP ID',
						render: (_, record) => record.napNo,
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Nama Customer',
						render: (_, record) => {
							return (
								<a
									onClick={() => {
										if (record.customer?.id) {
											openCustomerDetail(String(record.customer.id));
										}
									}}
								>
									{record?.customer?.value}
								</a>
							);
						},
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Jenis Plafon',
						render: (_, record) => record.plafonType,
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Tipe Limit',
						render: (_, record) => record.limitType,
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Sumber Order',
						render: (_, record) => record.orderSource,
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'FBDH',
						render: (_, record) => record.fbdhName,
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'FRM',
						render: (_, record) => record.frmName,
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Progress Saat Ini',
						render: (_, record) => record.currentProgress,
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Estimasi Tanggal Golive',
						render: (_, record) => {
							if (isMasked(record.status)) {
								return '-';
							}
							return (
								record.estimatedGoliveDate &&
								dayjs(record.estimatedGoliveDate).format('DD/MM/YYYY')
							);
						},
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Asset Type',
						render: (_, record) => record.assetType,
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Brand & Detail Jenis Unit',
						align: 'center',
						render: (_, record) => {
							if (!record.pipelineGoLiveId) {
								return <FileSearchOutlined style={{ color: '#bababa' }} />;
							}
							if (isMasked(record.status)) {
								return <FileSearchOutlined style={{ color: '#bababa' }} />;
							}

							return (
								<a
									onClick={async (e) => {
										e.preventDefault();
										if (getGoliveBrandMutation.isPending) return;

										const res = await getGoliveBrandMutation.mutateAsync(
											record.pipelineGoLiveId,
										);

										if (!res.data.length) {
											return notification.warning({
												message: 'Tidak ada brand & jenis unit tersimpan',
											});
										}

										brandDetailModal.open(res.data);
									}}
								>
									{getGoliveBrandMutation.isPending &&
									getGoliveBrandMutation.variables ===
										record.pipelineGoLiveId ? (
										<LoadingOutlined />
									) : (
										<FileSearchOutlined />
									)}
								</a>
							);
						},
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Skema Pembiayaan',
						render: (_, record) => {
							if (isMasked(record.status)) {
								return '-';
							}
							return record.fundingScheme;
						},
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Limit Diajukan',
						render: (_, record) => formatCurrency(record.requestedLimit),
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Limit Disetujui',
						render: (_, record) => formatCurrency(record.approvedLimit),
						onCell: (record) => (record.key ? {} : { hidden: true }),
					},
					{
						title: 'Estimasi NTF',
						render: (_, record) => {
							if (isMasked(record.status)) {
								return '-';
							}
							if (record.key) return formatCurrency(record.estimatedNtf);
							return <b>{formatCurrency(record.estimatedNtf)}</b>;
						},
					},
					{
						title: 'Jumlah Unit',
						render: (_, record) => {
							if (isMasked(record.status)) {
								return '-';
							}
							if (record.key) return formatCurrency(record.units);
							return <b>{formatCurrency(record.units)}</b>;
						},
					},
					{
						title: 'Last Modified',
						render: (_, record) => (
							<strong
								style={{
									color: '#BCBCBC',
									fontStyle: 'italic',
								}}
							>
								{`${record.lastModifiedAt ? dayjs(record.lastModifiedAt).format('DD/MM/YYYY, HH:mm') : ''} - ${record.lastModifiedByName ?? ''}`}
							</strong>
						),
					},
				]}
				pagination={false}
			/>
			<div
				style={{
					width: 200,
					marginTop: 50,
					marginLeft: 'auto',
				}}
			>
				<Button
					block
					type="primary"
					disabled={!data?.data.items.length}
					onClick={() => {
						exportToExcel(
							[
								{
									sheetName: 'Sheet 1',
									details: [
										...(data?.data.items.map((record) => {
											return {
												'Prospect ID': record.prospectId,
												'NAP ID': record.napNo,
												'Nama Customer': record.customer?.value,
												'Jenis Plafon': record.plafonType,
												'Sumber Order': record.orderSource,
												'FBDH': record.fbdhName,
												'FRM': record.frmName,
												'Progress Saat Ini': record.currentProgress,
												'Estimasi Tanggal Golive': record.estimatedGoliveDate
													? dayjs(record.estimatedGoliveDate).format(
															'DD/MM/YYYY',
														)
													: '',
												'Asset Type': record.assetType,
												'Brand & Detail Jenis Unit': '',
												'Skema Pembiayaan': record.fundingScheme,
												'Limit Diajukan': formatCurrency(record.requestedLimit),
												'Limit Disetujui': formatCurrency(record.approvedLimit),
												'Estimasi NTF': formatCurrency(record.estimatedNtf),
												'Jumlah Unit': formatCurrency(record.units),
												'Last Modified': `${record.lastModifiedAt ? dayjs(record.lastModifiedAt).format('DD/MM/YYYY, HH:mm') : ''} - ${record.lastModifiedByName ?? ''}`,
											};
										}) ?? []),
										...[
											{
												'Limit Disetujui': 'Total',
												'Estimasi NTF': data?.data?.totalEstimatedNtf ?? 0,
												'Jumlah Unit': data?.data?.totalUnits ?? 0,
											},
										],
									],
								},
							],
							`pipeline-golive-${Date.now()}`,
							true,
						);
					}}
				>
					DOWNLOAD PIPELINE
				</Button>
			</div>
			<BrandDetailModal {...brandDetailModal} />
		</Fragment>
	);
};

export default GoliveListPage;
